import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiFetch from "../../utlis/apiFetch";
import { forceLogout } from "../../utlis/forceLogout";
import { updateApplicationNoLoader, updateApplicationWithReason, updateApplicationStatus, updateApplication, reopenApplication } from "./adminLeadSlice";

const handleAuthError = (error, rejectWithValue) => {
  if (error.message === "Your session has expired") forceLogout();
  return rejectWithValue(error.message);
};

// Fetch history
export const fetchHistory = createAsyncThunk(
  "history/fetchHistory",
  async (
    { fromDate, toDate, category, status, countries, searchString, page = 1, limit = 10 } = {},
    { rejectWithValue, getState }
  ) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("No authentication token found");

      // Build query parameters
      // Countries comes first as per API requirement
      const params = new URLSearchParams();
      if (countries && countries.length > 0) params.append("countries", countries.join(","));
      if (status) params.append("status", status);
      if (category && category !== "All") params.append("visaCategory", category);
      if (fromDate) params.append("fromDate", fromDate);
      if (toDate) params.append("toDate", toDate);
      if (searchString && searchString.trim()) params.append("searchString", searchString.trim());
      params.append("page", page);
      params.append("limit", limit);

      // API request
      const response = await apiFetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/historyApplication?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      const data = response._data ?? await response.json();
      if (!response.ok)
        throw new Error(data.message || "Failed to fetch history");

      return data.body || [];
    } catch (err) {
      return handleAuthError(err, rejectWithValue);
    }
  }
);

// Redux Slice
const historySlice = createSlice({
  name: "history",
  initialState: {
    history: [],
    isLoading: false,
    error: null,
    filters: {
      fromMonth: null,
      fromYear: null,
      toMonth: null,
      toYear: null,
      category: "All",
      page: 1,
      limit: 10,
      lastFetchParams: null, // Track last fetch params to avoid redundant API calls
    },
  },
  reducers: {
    clearHistory: (state) => {
      state.history = [];
    },
    updateFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        // Reset page to 1 when filters change, unless page is explicitly provided
        page: action.payload.page || 1,
      };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchHistory.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.history = action.payload;
        // Store the last successful fetch parameters
        state.filters.lastFetchParams = {
          fromMonth: state.filters.fromMonth,
          fromYear: state.filters.fromYear,
          toMonth: state.filters.toMonth,
          toYear: state.filters.toYear,
          category: state.filters.category,
          page: state.filters.page,
          limit: state.filters.limit,
          status: action.meta && action.meta.arg ? action.meta.arg.status : undefined,
          countries: action.meta && action.meta.arg ? action.meta.arg.countries : undefined,
          searchString: action.meta && action.meta.arg ? action.meta.arg.searchString : undefined,
        };
      })
      .addCase(fetchHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload;
      })
      // Handle status updates from history table
      .addCase(updateApplicationNoLoader.fulfilled, (state, action) => {
        const updated = action.payload.data.body;
        if (Array.isArray(state.history.applications)) {
          state.history.applications = state.history.applications.map((app) =>
            app._id === updated._id ? updated : app
          );
        } else if (Array.isArray(state.history)) {
          state.history = state.history.map((app) =>
            app._id === updated._id ? updated : app
          );
        }
      })
      // When a lead status is updated to a terminal value, optimistically insert it into history
      .addCase(updateApplicationWithReason.fulfilled, (state, action) => {
        const updated = action.payload;
        const status = String(updated?.status || updated?.visaStatus || "");
        const isTerminal = [
          "Finalized",
          "Rejected",
          "Cancelled",
          "Withdrawn",
          "WithdrawnByAgent",
          "WithdrawnByClient",
        ].includes(status);
        if (isTerminal) {
          if (Array.isArray(state.history?.applications)) {
            const exists = state.history.applications.some((a) => a._id === updated._id);
            if (!exists) state.history.applications.unshift(updated);
          } else if (Array.isArray(state.history)) {
            const exists = state.history.some((a) => a._id === updated._id);
            if (!exists) state.history.unshift(updated);
          }
        }
      })
      .addCase(updateApplicationStatus.fulfilled, (state, action) => {
        const updated = action.payload;
        const status = String(updated?.status || updated?.visaStatus || "");
        const isTerminal = [
          "Finalized",
          "Rejected",
          "Cancelled",
          "Withdrawn",
          "WithdrawnByAgent",
          "WithdrawnByClient",
        ].includes(status);
        if (isTerminal) {
          if (Array.isArray(state.history?.applications)) {
            const exists = state.history.applications.some((a) => a._id === updated._id);
            if (!exists) state.history.applications.unshift(updated);
          } else if (Array.isArray(state.history)) {
            const exists = state.history.some((a) => a._id === updated._id);
            if (!exists) state.history.unshift(updated);
          }
        }
      })
      .addCase(updateApplication.fulfilled, (state, action) => {
        const updated = action.payload;
        if (Array.isArray(state.history?.applications)) {
          state.history.applications = state.history.applications.map((a) => (a._id === updated._id ? updated : a));
        } else if (Array.isArray(state.history)) {
          state.history = state.history.map((a) => (a._id === updated._id ? updated : a));
        }
      })
      // Handle application reopen - remove from history
      .addCase(reopenApplication.fulfilled, (state, action) => {
        const reopenedId = action.payload._id;
        if (Array.isArray(state.history.applications)) {
          state.history.applications = state.history.applications?.filter(
            (app) => app._id !== reopenedId
          );
        } else if (Array.isArray(state.history)) {
          state.history = state.history.filter((app) => app._id !== reopenedId);
        }
      });
  },
});

export const { clearHistory, updateFilters, setPage } = historySlice.actions;
export default historySlice.reducer;
